/* Shared optional widget/media loaders. No checkout, consent or tracking changes. */
(() => {
  if (window.NetlagerSharedMedia) return;
  const instances = new Map();
  const pending = new Map();
  const library = (key, url, ready) => {
    if (ready()) return Promise.resolve();
    if (pending.has(key)) return pending.get(key);
    const promise = new Promise((resolve, reject) => {
      let script = [...document.scripts].find(node => node.src === url || (key === 'trustpilot' && node.src.includes('widget.trustpilot.com/bootstrap/')));
      const existing = !!script;
      script ||= document.createElement('script');
      const done = () => { clearTimeout(timer); ready() ? resolve() : reject(new Error(key + ' did not initialize')); };
      const fail = () => { clearTimeout(timer); reject(new Error(key + ' could not load')); };
      const timer = setTimeout(fail, 15000);
      script.addEventListener('load', done, { once: true });
      script.addEventListener('error', fail, { once: true });
      if (!existing) { script.src = url; script.async = true; document.head.append(script); }
    });
    pending.set(key, promise);
    promise.catch(() => pending.delete(key));
    return promise;
  };
  function fallbackMedia(root) {
    root.querySelectorAll('.lazy[data-src]').forEach(node => {
      node.src = node.dataset.src;
      if (node.tagName === 'VIDEO') node.load();
    });
  }
  async function mountHero(root) {
    if (instances.has(root) || !root.querySelector('.lazy[data-src], .lazy[data-srcset], .lazy source[data-src]')) return;
    instances.set(root, null);
    try {
      await library('lazyload', 'https://cdn.jsdelivr.net/npm/vanilla-lazyload@19.1.3/dist/lazyload.min.js', () => !!window.LazyLoad);
      if (!root.isConnected || !instances.has(root)) return;
      const instance = new window.LazyLoad({}, root.querySelectorAll('.lazy'));
      instances.set(root, instance);
    } catch (_) {
      if (root.isConnected && instances.has(root)) fallbackMedia(root);
    }
  }
  function icecatLibrary() {
    if (window.IcecatLive?.getDatasheet) return Promise.resolve();
    if (pending.has('icecat')) return pending.get('icecat');
    const promise = new Promise((resolve, reject) => {
      const url = 'https://live.icecat.biz/js/live-current-2.js';
      let script = [...document.scripts].find(node => node.src === url);
      const created = !script;
      script ||= document.createElement('script');
      const cleanup = () => { clearTimeout(timer); window.removeEventListener('liveload', ready); script.removeEventListener('error', fail); script.removeEventListener('load', ready); };
      const ready = () => { if (window.IcecatLive?.getDatasheet) { cleanup(); resolve(); } };
      const fail = () => { cleanup(); if (created) script.remove(); reject(new Error('Icecat could not load')); };
      const timer = setTimeout(fail, 15000);
      window.addEventListener('liveload', ready);
      script.addEventListener('load', ready);
      script.addEventListener('error', fail, { once: true });
      if (created) { script.src = url; script.async = true; document.head.append(script); }
      ready();
    });
    pending.set('icecat', promise);
    promise.catch(() => pending.delete('icecat'));
    return promise;
  }
  function mountIcecat(root) {
    if (instances.has(root) || !root.dataset.gtin) return;
    let observer, destroyed = false, started = false;
    instances.set(root, { destroy() { destroyed = true; observer?.disconnect(); } });
    const load = async () => {
      if (started || destroyed) return;
      started = true;
      observer?.disconnect();
      root.setAttribute('aria-busy', 'true');
      try {
        await icecatLibrary();
        if (!destroyed && root.isConnected) window.IcecatLive.getDatasheet(
          { productstory: '#' + CSS.escape(root.id) },
          { GTIN: root.dataset.gtin, UserName: 'netlager' },
          root.dataset.icecatLanguage || 'en'
        );
      } catch (_) { root.dataset.icecatError = 'unavailable'; }
      finally { root.removeAttribute('aria-busy'); }
    };
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) load(); }, { rootMargin: '200px 0px' });
      observer.observe(root);
    } else load();
  }
  function mount(scope = document) {
    const roots = [...scope.querySelectorAll('[data-netlager-hero-lazy]')];
    if (scope.matches?.('[data-netlager-hero-lazy]')) roots.unshift(scope);
    roots.forEach(mountHero);
    const icecatRoots = [...scope.querySelectorAll('[data-netlager-icecat]')];
    if (scope.matches?.('[data-netlager-icecat]')) icecatRoots.unshift(scope);
    icecatRoots.forEach(mountIcecat);
    const widgets = [...scope.querySelectorAll('.trustpilot-widget')];
    if (scope.matches?.('.trustpilot-widget')) widgets.unshift(scope);
    if (widgets.length) {
      library('trustpilot', 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js', () => !!window.Trustpilot?.loadFromElement)
        .then(() => {
          // Existing app loaders may have run before these widgets were parsed.
          widgets.filter(node => node.isConnected && !node.querySelector('iframe')).forEach(node => window.Trustpilot.loadFromElement(node));
        }).catch(() => { /* Native Trustpilot links remain available if the service fails. */ });
    }
  }
  function unmount(scope) {
    for (const [root, instance] of instances) if (root === scope || scope.contains(root)) {
      instance?.destroy();
      instances.delete(root);
    }
  }
  window.NetlagerSharedMedia = { mount, unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
})();
